import { PropsWithChildren } from 'react';
import { useActions } from 'context/ActionsContext';

interface HasActionProps {
  /** Single action or array of actions to check */
  action: string | string[];
  /** If true, requires ALL actions (default: false = any action) */
  requireAll?: boolean;
  /** Optional fallback to render when action is not granted */
  fallback?: React.ReactNode;
}

/**
 * Conditionally renders children based on whether the user has the required action(s).
 * Similar to HasRole but checks action-based permissions instead of roles.
 *
 * @example
 * // Single action
 * <HasAction action="settings:write">
 *   <EditButton />
 * </HasAction>
 *
 * @example
 * // Any of multiple actions
 * <HasAction action={["settings:write", "settings:admin"]}>
 *   <EditButton />
 * </HasAction>
 *
 * @example
 * // All actions required
 * <HasAction action={["settings:write", "settings:delete"]} requireAll>
 *   <DangerousButton />
 * </HasAction>
 *
 * @example
 * // With fallback
 * <HasAction action="settings:write" fallback={<DisabledButton />}>
 *   <EditButton />
 * </HasAction>
 */
export function HasAction({ children, action, requireAll = false, fallback = null }: PropsWithChildren<HasActionProps>) {
  const { hasAction, hasAnyAction, hasAllActions, isReady } = useActions();

  // Don't render anything while loading
  if (!isReady) {
    return <>{fallback}</>;
  }

  const actions = Array.isArray(action) ? action : [action];
  const hasPermission = requireAll
    ? hasAllActions(actions)
    : actions.length === 1
      ? hasAction(actions[0])
      : hasAnyAction(actions);

  if (!hasPermission) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}

/**
 * Returns whether the user has the specified action.
 * Use this when you need the boolean value directly instead of conditional rendering.
 *
 * Note: This is a wrapper around useActions for convenience.
 * For more complex checks, use useActions directly.
 */
export { useHasAction, useHasAnyAction } from 'context/ActionsContext';
